import React from 'react';
import { Controller } from 'react-hook-form';
import { Card, TextField, Typography } from '@mui/material';

// Import validation utilities
import { getValidationRules } from '../utils/surveyValidation';

/**
 * SurveySummary Component
 * 
 * Renders the final summary tab of the survey where users provide
 * overall assessments of strengths and opportunities for improvement.
 * 
 * This component displays two required text areas:
 * - Strengths: What's working well at the facility
 * - Opportunities: Areas that need improvement or attention
 * 
 * @param {Object} props - Component props
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} props.errors - Form validation errors
 */
const SurveySummary = ({ control, errors }) => {
  return (
    <Card style={{ height: '100%', padding: 16 }}>
      {/* Strengths Section */}
      <Typography 
        variant="h6" 
        style={{ 
          fontFamily: 'Lato', 
          fontSize: 18,
          fontWeight: 'bold',
          marginBottom: 16,
          color: '#2e7d32'
        }}
      >
        Strengths
      </Typography>
      
      <Typography 
        variant="body2" 
        color="textSecondary" 
        style={{ 
          fontFamily: 'Lato',
          marginBottom: 12
        }}
      >
        Please describe what is working well at this location. Include specific examples 
        of excellent practices, well-maintained areas, or outstanding performance.
      </Typography>
      
      <Controller
        name="strengths"
        control={control}
        rules={getValidationRules('required-text', true)}
        render={({ field }) => (
          <TextField
            {...field}
            multiline
            minRows={8}
            maxRows={12}
            fullWidth
            variant="outlined"
            placeholder="Describe the strengths and positive aspects observed during this walkthrough..."
            error={!!errors.strengths}
            helperText={errors.strengths?.message || ''}
            sx={{ mb: 4 }}
          />
        )}
      />

      {/* Opportunities Section */}
      <Typography 
        variant="h6" 
        style={{ 
          fontFamily: 'Lato', 
          fontSize: 18,
          fontWeight: 'bold',
          marginBottom: 16,
          marginTop: 32,
          color: '#ed6c02'
        }}
      >
        Opportunities for Improvement
      </Typography>
      
      <Typography 
        variant="body2" 
        color="textSecondary" 
        style={{ 
          fontFamily: 'Lato',
          marginBottom: 12
        }}
      >
        Please describe areas that need attention or improvement. Include suggestions 
        for how these opportunities could be addressed and any potential benefits.
      </Typography>
      
      <Controller
        name="opportunities"
        control={control}
        rules={getValidationRules('required-text', true)}
        render={({ field }) => (
          <TextField
            {...field}
            multiline
            minRows={8}
            maxRows={12}
            fullWidth
            variant="outlined"
            placeholder="Describe opportunities for improvement and recommendations for addressing them..."
            error={!!errors.opportunities}
            helperText={errors.opportunities?.message || ''}
            sx={{ mb: 2 }}
          />
        )}
      />

      {/* Instructions */}
      <Typography 
        variant="body2" 
        color="textSecondary"
        style={{ 
          fontFamily: 'Lato',
          fontSize: 12,
          fontStyle: 'italic',
          marginTop: 16,
          padding: 12,
          backgroundColor: '#f5f5f5',
          borderRadius: 4,
          border: '1px solid #e0e0e0'
        }}
      >
        <strong>Note:</strong> Both sections are required and should provide specific, 
        actionable insights. This summary will be included in reports and used for 
        operational planning and improvement initiatives.
      </Typography>
    </Card>
  );
};

export default SurveySummary;